import { SalesInvoiceItemBase } from "../abstract/sales-invoice-item-base.model";
import { PaymentSchedule } from "../payment-schedule/schedule.model";
import { OmitField } from "src/app/@shared/decorators/api-field.decorator";


/**
 * Document d'origine d'une ligne du contrat en vigueur (devis racine ou avenant
 * effectif), porté par les seules lignes de `Quotation.contractItems`.
 */
export interface QuotationItemSourceDocument {
    id: number;
    reference: string;
    type: { code: string, label: string } | null;
    addendumNumber: number | null;
    addendumEffectiveDate: string | null;
}

/**
 * Valeurs consolidées d'une **prestation** du contrat — la lignée entière à travers
 * les restatements successifs, et non la seule ligne qui la porte.
 *
 * Servi à deux endroits : sur les lignes de `Quotation.contractItems` (le contrat en
 * vigueur) et sur les lignes d'un **brouillon** d'avenant (le contrat **projeté**,
 * tel qu'il serait si le brouillon prenait effet). Nul partout ailleurs.
 */
export interface QuotationItemConsolidated {
    quantity: number;
    quantityBilled: number;
    netAmount: number;
    netAmountOrg: number;
    netAmountBilled: number;
    netAmountBilledOrg: number;
    taxAmount: number;
    taxAmountOrg: number;
    taxAmountBilled: number;
    taxAmountBilledOrg: number;
    grossAmount: number;
    grossAmountOrg: number;
    grossAmountBilled: number;
    grossAmountBilledOrg: number;
}

export class QuotationItem extends SalesInvoiceItemBase {

    static override CLASS_NAME: string = 'QuotationItem';

    private _grossAmountBilled!: number;
    private _grossAmountBilledOrg!: number;
    private _grossAmountDownpaid!: number;
    private _grossAmountDownpaidOrg!: number;
    private _grossAmountDownpaymentRemaining!: number;
    private _grossAmountDownpaymentRemainingOrg!: number;
    private _grossAmountDownpaymentUsed!: number;
    private _grossAmountDownpaymentUsedOrg!: number;
    private _changeMarker!: { code: string, label: string } | null;
    private _consolidated!: QuotationItemConsolidated | null;
    private _contractItemBilledQuantity!: number;
    private _contractItemCap!: number;
    private _contractItemId!: number;
    private _contractItemPreviousCap!: number;
    private _grossAmountToBill!: number;
    private _grossAmountToBillOrg!: number;
    private _isClosed!: boolean;
    private _netAmountBilled!: number;
    private _netAmountBilledOrg!: number;
    private _netAmountDownpaid!: number;
    private _netAmountDownpaidOrg!: number;
    private _netAmountDownpaymentRemaining!: number;
    private _netAmountDownpaymentRemainingOrg!: number;
    private _netAmountDownpaymentUsed!: number;
    private _netAmountDownpaymentUsedOrg!: number;
    private _netAmountToBill!: number;
    private _netAmountToBillOrg!: number;
    private _progressionCap!: number;
    private _progressionCapFloor!: number;
    private _quantityBilled!: number;
    private _schedule!: PaymentSchedule;
    private _sourceDocument!: QuotationItemSourceDocument;
    private _taxAmountBilled!: number;
    private _taxAmountBilledOrg!: number;
    private _taxAmountDownpaid!: number;
    private _taxAmountDownpaidOrg!: number;
    private _taxAmountDownpaymentRemaining!: number;
    private _taxAmountDownpaymentRemainingOrg!: number;
    private _taxAmountDownpaymentUsed!: number;
    private _taxAmountDownpaymentUsedOrg!: number;
    private _taxAmountToBill!: number;
    private _taxAmountToBillOrg!: number;

    /**
     * Ce que la ligne représente dans le restatement porté par son avenant :
     * `none` / `new` / `modified` / `deleted`. Recalculé par le serveur à chaque
     * enregistrement du brouillon — jamais renseigné par le front.
     */
    @OmitField()
    public get changeMarker(): { code: string, label: string } | null {
        return this._changeMarker;
    }

    public set changeMarker(value: any) {
        this._changeMarker = value ?? null;
    }

    /**
     * Valeurs de la **prestation entière** (cf. `QuotationItemConsolidated`). Nul
     * hors ligne de contrat et hors brouillon d'avenant.
     */
    @OmitField()
    public get consolidated(): QuotationItemConsolidated | null {
        return this._consolidated;
    }

    public set consolidated(value: any) {
        this._consolidated = value ?? null;
    }

    /**
     * Quantité déjà facturée de la ligne mère **immédiate**, figée à la compilation.
     *
     * ⚠️ Ne jamais s'en servir pour reconstituer la quantité du contrat : elle ignore
     * la part figée par les avenants antérieurs — cf. {@link contractQuantity}.
     */
    @OmitField()
    public get contractItemBilledQuantity(): number {
        return this._contractItemBilledQuantity;
    }

    public set contractItemBilledQuantity(value: any) {
        this._setValue('_contractItemBilledQuantity', value, 'float');
    }

    @OmitField()
    public get contractItemCap(): number {
        return this._contractItemCap;
    }

    public set contractItemCap(value: any) {
        this._setValue('_contractItemCap', value, 'float');
    }

    @OmitField()
    public get contractItemId(): number {
        return this._contractItemId;
    }

    public set contractItemId(value: any) {
        this._setValue('_contractItemId', value, 'integer');
    }

    @OmitField()
    public get contractItemPreviousCap(): number {
        return this._contractItemPreviousCap;
    }

    public set contractItemPreviousCap(value: any) {
        this._setValue('_contractItemPreviousCap', value, 'float');
    }

    /**
     * Quantité de la prestation **au contrat** — celle qui se lit et se saisit, par
     * opposition à `quantity` qui n'est, sur un avenant, que le **reliquat** (cible
     * moins déjà facturé).
     *
     * ⚠️ C'est `consolidated.quantity` et **jamais** `contractItemBilledQuantity +
     * quantity` : cette somme ignore la part figée par les avenants antérieurs. Sur
     * une racine de 5 dont 2 facturées, portée à 8 par A1, puis un brouillon A2, la
     * somme naïve donne 8 là où le contrat est à 10.
     */
    @OmitField()
    public get contractQuantity(): number {
        return this._toNumber(this._consolidated ? this._consolidated.quantity : this.quantity);
    }

    /*
     * Montants de la prestation **au contrat**, symétriques de `contractQuantity`.
     *
     * ⚠️ Les champs directs d'une entrée de `contractItems` décrivent sa seule ligne
     * de tête — le reliquat porté par le dernier avenant — et pas la prestation. Tout
     * écran de suivi lit donc ces accesseurs, jamais `netAmount` & co. directement.
     * Sur un devis sans avenant, `consolidated` est nul et ils dégénèrent sur la
     * ligne : les deux lectures coïncident.
     *
     * Le mélange des bases est **voulu** : sur une révision de prix, la part déjà
     * facturée reste à l'ancien prix (2 × 100 + 6 × 120 = 920, et non 8 × 120).
     * C'est ce que dira le PDF de l'avenant — même service côté serveur.
     */

    @OmitField()
    public get contractGrossAmount(): number {
        return this._toNumber(this._consolidated ? this._consolidated.grossAmount : this.grossAmount);
    }

    @OmitField()
    public get contractGrossAmountBilled(): number {
        return this._toNumber(this._consolidated ? this._consolidated.grossAmountBilled : this.grossAmountBilled);
    }

    @OmitField()
    public get contractNetAmount(): number {
        return this._toNumber(this._consolidated ? this._consolidated.netAmount : this.netAmount);
    }

    @OmitField()
    public get contractNetAmountBilled(): number {
        return this._toNumber(this._consolidated ? this._consolidated.netAmountBilled : this.netAmountBilled);
    }

    @OmitField()
    public get contractQuantityBilled(): number {
        return this._toNumber(this._consolidated ? this._consolidated.quantityBilled : this.quantityBilled);
    }

    @OmitField()
    public get contractTaxAmount(): number {
        return this._toNumber(this._consolidated ? this._consolidated.taxAmount : this.taxAmount);
    }

    @OmitField()
    public get contractTaxAmountBilled(): number {
        return this._toNumber(this._consolidated ? this._consolidated.taxAmountBilled : this.taxAmountBilled);
    }

    /**
     * Part de la prestation **gelée par l'ascendance** : ce que les documents
     * antérieurs portent déjà et que le brouillon ne peut plus bouger.
     *
     * C'est la constante de conversion entre la valeur saisie et ce qui part au
     * serveur : `quantity = totalSaisi − frozenQuantity`. Elle ne varie pas pendant
     * l'édition — seulement d'un enregistrement à l'autre, quand le serveur
     * recompile.
     *
     * Vaut **zéro sur une ligne neuve** : elle n'a aucune ascendance, sa quantité
     * saisie est déjà celle du contrat.
     */
    @OmitField()
    public get frozenQuantity(): number {
        return this.contractQuantity - this._toNumber(this.quantity);
    }

    @OmitField()
    public get grossAmountBilled(): number {
        return this._grossAmountBilled;
    }

    public set grossAmountBilled(value: any) {
        this._setValue('_grossAmountBilled', value, 'float');
    }

    @OmitField()
    public get grossAmountBilledOrg(): number {
        return this._grossAmountBilledOrg;
    }

    public set grossAmountBilledOrg(value: any) {
        this._setValue('_grossAmountBilledOrg', value, 'float');
    }

    @OmitField()
    public get grossAmountDownpaid(): number {
        return this._grossAmountDownpaid;
    }

    public set grossAmountDownpaid(value: any) {
        this._setValue('_grossAmountDownpaid', value, 'float');
    }

    @OmitField()
    public get grossAmountDownpaidOrg(): number {
        return this._grossAmountDownpaidOrg;
    }

    public set grossAmountDownpaidOrg(value: any) {
        this._setValue('_grossAmountDownpaidOrg', value, 'float');
    }

    @OmitField()
    public get grossAmountDownpaymentRemaining(): number {
        return this._grossAmountDownpaymentRemaining;
    }

    public set grossAmountDownpaymentRemaining(value: any) {
        this._setValue('_grossAmountDownpaymentRemaining', value, 'float');
    }

    @OmitField()
    public get grossAmountDownpaymentRemainingOrg(): number {
        return this._grossAmountDownpaymentRemainingOrg;
    }

    public set grossAmountDownpaymentRemainingOrg(value: any) {
        this._setValue('_grossAmountDownpaymentRemainingOrg', value, 'float');
    }

    @OmitField()
    public get grossAmountDownpaymentUsed(): number {
        return this._grossAmountDownpaymentUsed;
    }

    public set grossAmountDownpaymentUsed(value: any) {
        this._setValue('_grossAmountDownpaymentUsed', value, 'float');
    }

    @OmitField()
    public get grossAmountDownpaymentUsedOrg(): number {
        return this._grossAmountDownpaymentUsedOrg;
    }

    public set grossAmountDownpaymentUsedOrg(value: any) {
        this._setValue('_grossAmountDownpaymentUsedOrg', value, 'float');
    }

    @OmitField()
    public get grossAmountToBill(): number {
        return this._grossAmountToBill;
    }

    public set grossAmountToBill(value: any) {
        this._setValue('_grossAmountToBill', value, 'float');
    }

    @OmitField()
    public get grossAmountToBillOrg(): number {
        return this._grossAmountToBillOrg;
    }

    public set grossAmountToBillOrg(value: any) {
        this._setValue('_grossAmountToBillOrg', value, 'float');
    }

    @OmitField()
    public get isClosed(): boolean {
        return this._isClosed;
    }

    public set isClosed(value: any) {
        this._setValue('_isClosed', value, 'boolean');
    }

    @OmitField()
    public get netAmountBilled(): number {
        return this._netAmountBilled;
    }

    public set netAmountBilled(value: any) {
        this._setValue('_netAmountBilled', value, 'float');
    }

    @OmitField()
    public get netAmountBilledOrg(): number {
        return this._netAmountBilledOrg;
    }

    public set netAmountBilledOrg(value: any) {
        this._setValue('_netAmountBilledOrg', value, 'float');
    }

    @OmitField()
    public get netAmountDownpaid(): number {
        return this._netAmountDownpaid;
    }

    public set netAmountDownpaid(value: any) {
        this._setValue('_netAmountDownpaid', value, 'float');
    }

    @OmitField()
    public get netAmountDownpaidOrg(): number {
        return this._netAmountDownpaidOrg;
    }

    public set netAmountDownpaidOrg(value: any) {
        this._setValue('_netAmountDownpaidOrg', value, 'float');
    }

    @OmitField()
    public get netAmountDownpaymentRemaining(): number {
        return this._netAmountDownpaymentRemaining;
    }

    public set netAmountDownpaymentRemaining(value: any) {
        this._setValue('_netAmountDownpaymentRemaining', value, 'float');
    }

    @OmitField()
    public get netAmountDownpaymentRemainingOrg(): number {
        return this._netAmountDownpaymentRemainingOrg;
    }

    public set netAmountDownpaymentRemainingOrg(value: any) {
        this._setValue('_netAmountDownpaymentRemainingOrg', value, 'float');
    }

    @OmitField()
    public get netAmountDownpaymentUsed(): number {
        return this._netAmountDownpaymentUsed;
    }

    public set netAmountDownpaymentUsed(value: any) {
        this._setValue('_netAmountDownpaymentUsed', value, 'float');
    }

    @OmitField()
    public get netAmountDownpaymentUsedOrg(): number {
        return this._netAmountDownpaymentUsedOrg;
    }

    public set netAmountDownpaymentUsedOrg(value: any) {
        this._setValue('_netAmountDownpaymentUsedOrg', value, 'float');
    }

    @OmitField()
    public get netAmountToBill(): number {
        return this._netAmountToBill;
    }

    public set netAmountToBill(value: any) {
        this._setValue('_netAmountToBill', value, 'float');
    }

    @OmitField()
    public get netAmountToBillOrg(): number {
        return this._netAmountToBillOrg;
    }

    public set netAmountToBillOrg(value: any) {
        this._setValue('_netAmountToBillOrg', value, 'float');
    }

    @OmitField()
    public get progressionCap(): number {
        return this._progressionCap;
    }

    public set progressionCap(value: any) {
        this._setValue('_progressionCap', value, 'float');
    }

    @OmitField()
    public get progressionCapFloor(): number {
        return this._progressionCapFloor;
    }

    public set progressionCapFloor(value: any) {
        this._setValue('_progressionCapFloor', value, 'float');
    }

    @OmitField()
    public get quantityBilled(): number {
        return this._quantityBilled;
    }

    public set quantityBilled(value: any) {
        this._setValue('_quantityBilled', value, 'float');
    }

    public get schedule(): PaymentSchedule {
        return this._schedule;
    }

    public set schedule(value: any) {
        this._setValue('_schedule', value, 'PaymentSchedule');
    }

    /**
     * Document d'origine de la ligne — renseigné uniquement sur les lignes servies
     * par `Quotation.contractItems`, nul sur les lignes du document lui-même.
     */
    @OmitField()
    public get sourceDocument(): QuotationItemSourceDocument {
        return this._sourceDocument;
    }

    public set sourceDocument(value: any) {
        this._sourceDocument = value;
    }

    @OmitField()
    public get taxAmountBilled(): number {
        return this._taxAmountBilled;
    }

    public set taxAmountBilled(value: any) {
        this._setValue('_taxAmountBilled', value, 'float');
    }

    @OmitField()
    public get taxAmountBilledOrg(): number {
        return this._taxAmountBilledOrg;
    }

    public set taxAmountBilledOrg(value: any) {
        this._setValue('_taxAmountBilledOrg', value, 'float');
    }

    @OmitField()
    public get taxAmountDownpaid(): number {
        return this._taxAmountDownpaid;
    }

    public set taxAmountDownpaid(value: any) {
        this._setValue('_taxAmountDownpaid', value, 'float');
    }

    @OmitField()
    public get taxAmountDownpaidOrg(): number {
        return this._taxAmountDownpaidOrg;
    }

    public set taxAmountDownpaidOrg(value: any) {
        this._setValue('_taxAmountDownpaidOrg', value, 'float');
    }

    @OmitField()
    public get taxAmountDownpaymentRemaining(): number {
        return this._taxAmountDownpaymentRemaining;
    }

    public set taxAmountDownpaymentRemaining(value: any) {
        this._setValue('_taxAmountDownpaymentRemaining', value, 'float');
    }

    @OmitField()
    public get taxAmountDownpaymentRemainingOrg(): number {
        return this._taxAmountDownpaymentRemainingOrg;
    }

    public set taxAmountDownpaymentRemainingOrg(value: any) {
        this._setValue('_taxAmountDownpaymentRemainingOrg', value, 'float');
    }

    @OmitField()
    public get taxAmountDownpaymentUsed(): number {
        return this._taxAmountDownpaymentUsed;
    }

    public set taxAmountDownpaymentUsed(value: any) {
        this._setValue('_taxAmountDownpaymentUsed', value, 'float');
    }

    @OmitField()
    public get taxAmountDownpaymentUsedOrg(): number {
        return this._taxAmountDownpaymentUsedOrg;
    }

    public set taxAmountDownpaymentUsedOrg(value: any) {
        this._setValue('_taxAmountDownpaymentUsedOrg', value, 'float');
    }

    @OmitField()
    public get taxAmountToBill(): number {
        return this._taxAmountToBill;
    }

    public set taxAmountToBill(value: any) {
        this._setValue('_taxAmountToBill', value, 'float');
    }

    @OmitField()
    public get taxAmountToBillOrg(): number {
        return this._taxAmountToBillOrg;
    }

    public set taxAmountToBillOrg(value: any) {
        this._setValue('_taxAmountToBillOrg', value, 'float');
    }

    /**
     * Conversion défensive des lectures `contract*`.
     *
     * ⚠️ Ne pas remplacer par un `Number()` nu : sur une ligne **neuve**, les champs
     * ne sont pas encore renseignés et `Number(undefined)` vaut `NaN`. Un `NaN` se
     * propage ensuite en silence — `frozenQuantity` devient `NaN`, la conversion de
     * la saisie rend `NaN`, et `_setValue(…, 'float')` le range en `null` : la
     * quantité tombe à zéro sans le moindre message.
     */
    private _toNumber(value: any): number {
        return Number(value) || 0;
    }

}
